import { Check, Clock, Inbox } from "lucide-react";

import { withAppTimeZone } from "@/lib/timezone";
import { isThreadAiPaused } from "@/lib/thread-flags";
// DISABLED (for now): thread assignment — assignment lives on tasks only.
// import { getThreadAssignee, isAssignedToUser } from "@/lib/thread-assignee";
import {
  getBotAckWaitingMinutes,
  getPatientReplyWaitingMinutes,
  getWhatsAppInboxWaitingMinutes,
  isPeerToPeerThread,
  normalizeLastMessageSender,
} from "@/lib/messages/messages-helpers";

/**
 * Workflow status: ONE label per thread — `open → assigned → pending → done` — independent of
 * AI handling (does the bot answer?) and patient tags (who are we talking to?).
 *
 * The server stores only a manual override (`metadata.status` + audit stamps); the effective
 * value is derived here at read time so the list, board, header chip and panel can never
 * disagree. See getEffectiveThreadStatus.
 *
 * NOTE (Tailwind v4): every class below is a complete literal string — the scanner cannot see
 * constructed names like `bg-${tone}-500` (same constraint as sidebar-icon-colors.js).
 */
// DISABLED (for now): "assigned" — thread assignment is hidden (assignment lives on
// tasks only), so the status can no longer derive or be set; legacy "assigned"
// overrides normalize back to the derived value.
export const THREAD_STATUSES = Object.freeze(["open", /* "assigned", */ "pending", "done"]);

export const THREAD_STATUS_CATALOG = Object.freeze({
  open: {
    key: "open",
    label: "Open",
    icon: Inbox,
    // 3px row rail + avatar status dot.
    railClassName: "bg-slate-400 dark:bg-slate-500",
    dotClassName: "bg-slate-500 dark:bg-slate-400",
    chipClassName:
      "border border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-500/40 dark:bg-slate-800/60 dark:text-slate-300",
    // Triage-board column header tile + body tint.
    columnTitle: "Open · unclaimed",
    columnTileClassName: "bg-slate-500/15 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300",
    columnTintClassName: "bg-slate-100/70 dark:bg-slate-900/40",
    // Active segment in the 4-segment status control.
    segmentActiveClassName: "bg-slate-600 text-white shadow-sm",
  },
  // DISABLED (for now): the "Assigned" status — see THREAD_STATUSES above.
  // assigned: {
  //   key: "assigned",
  //   label: "Assigned",
  //   icon: UserRound,
  //   railClassName: "bg-indigo-600 dark:bg-indigo-500",
  //   dotClassName: "bg-indigo-600 dark:bg-indigo-500",
  //   chipClassName:
  //     "border border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-950/60 dark:text-indigo-300",
  //   columnTitle: "Assigned · working",
  //   columnTileClassName: "bg-indigo-500/15 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300",
  //   columnTintClassName: "bg-indigo-50/50 dark:bg-indigo-950/25",
  //   segmentActiveClassName: "bg-indigo-600 text-white shadow-sm",
  // },
  pending: {
    key: "pending",
    label: "Pending",
    icon: Clock,
    railClassName: "bg-amber-500 dark:bg-amber-400",
    dotClassName: "bg-amber-500 dark:bg-amber-400",
    chipClassName:
      "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-300",
    columnTitle: "Pending · waiting",
    columnTileClassName: "bg-amber-500/15 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300",
    columnTintClassName: "bg-amber-50/50 dark:bg-amber-950/25",
    segmentActiveClassName: "bg-amber-500 text-white shadow-sm",
  },
  done: {
    key: "done",
    label: "Done",
    icon: Check,
    // Done rows carry no rail (they read as settled); the avatar dot stays emerald.
    railClassName: "",
    dotClassName: "bg-emerald-500 dark:bg-emerald-400",
    chipClassName:
      "border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-300",
    columnTitle: "Done · today",
    columnTileClassName: "bg-emerald-500/15 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300",
    columnTintClassName: "bg-emerald-50/40 dark:bg-emerald-950/20",
    segmentActiveClassName: "bg-emerald-600 text-white shadow-sm",
  },
});

export function getThreadStatusDefinition(status) {
  return THREAD_STATUS_CATALOG[String(status || "").trim()] || null;
}

function metadataString(thread, key) {
  const raw = thread?.metadata?.[key];
  return typeof raw === "string" ? raw.trim() : "";
}

/** metadata.pendingUntil as epoch ms, or null when absent/invalid. */
export function getThreadPendingUntilMs(thread) {
  const iso = metadataString(thread, "pendingUntil");
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

export function getThreadPendingReason(thread) {
  return metadataString(thread, "pendingReason");
}

export function getThreadStatusChangedAtMs(thread) {
  const iso = metadataString(thread, "statusChangedAt");
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * True when a new inbound patient message arrived after the status was stamped: the anchor
 * message id (recorded by the status endpoint for done/pending) no longer matches the
 * thread's last message, and that last message came from the patient. Threads without a
 * resolved patient fall back to "any non-bot sender" (good enough — staff replies normally
 * also flip flags/assignee, which dominate derivation).
 */
function hasInboundPatientMessageSinceStatus(thread) {
  const anchor = metadataString(thread, "statusMessageId");
  const lastId = String(thread?.lastMessageId || "").trim();
  if (!anchor || !lastId || anchor === lastId) return false;
  const sender = normalizeLastMessageSender(thread);
  if (!sender || sender.kind === "bot") return false;
  const patientId = String(thread?.patientUserId || "").trim();
  if (patientId) return sender.userId === patientId;
  return true;
}

/**
 * The ONE effective-status rule (used by rows, header chip, panel, board, lenses):
 *
 *   manual override (metadata.status) — with auto-reopen:
 *     done    → cleared when the patient writes again (re-opens by itself)
 *     pending → cleared when pendingUntil passes OR the patient writes again
 *   else AI paused (locked/staff)→ "open"   (sits in the shared queue)
 *   else                         → null     (bot answering — no rail/dot, not on the board)
 *
 * DISABLED (for now): the assignee → "assigned" derivation — thread assignment is
 * hidden; assignment lives on tasks only.
 */
export function getEffectiveThreadStatus(thread, nowMs = Date.now()) {
  if (!thread) return null;
  // Peer-to-peer conversations have no workflow status. "Open / pending / done" describes a clinic
  // working through a patient's request; two people messaging each other are not a queue. Enforced
  // here rather than at the call sites so no future caller can reintroduce it.
  if (isPeerToPeerThread(thread)) return null;
  let manual = metadataString(thread, "status");
  if (!THREAD_STATUSES.includes(manual)) manual = "";

  if (manual === "done" && hasInboundPatientMessageSinceStatus(thread)) {
    manual = "";
  }
  if (manual === "pending") {
    const untilMs = getThreadPendingUntilMs(thread);
    const expired = untilMs != null && untilMs <= nowMs;
    if (expired || hasInboundPatientMessageSinceStatus(thread)) {
      manual = "";
    }
  }
  if (manual) return manual;
  // if (getThreadAssignee(thread)) return "assigned";
  if (isThreadAiPaused(thread)) return "open";
  return null;
}

/**
 * Lens ids for the Messages list: the calm default and the triage piles.
 * DISABLED (for now): "mine" — thread assignment is hidden; persisted "mine"
 * preferences normalize back to "all".
 */
export const THREAD_LENSES = Object.freeze(["all", "needs" /* , "mine" */]);

export function normalizeThreadLens(value) {
  const lens = String(value || "").trim();
  return THREAD_LENSES.includes(lens) ? lens : "all";
}

/**
 * Lens predicates over one loaded thread row.
 *  - all   → everything (chronological, newest first — the calm default)
 *  - needs → today's triage piles: "Waiting for you" (AI paused) + "Unanswered bot replies"
 *
 * DISABLED (for now): the "mine" lens (threads assigned to the current user) —
 * thread assignment is hidden; assignment lives on tasks only.
 */
export function threadMatchesLens(
  thread,
  lens,
  {
    membership,
    nowMs,
    botAckThresholdMinutes,
    botAckMaxHours,
    // Undefined falls through to getPatientReplyWaitingMinutes' own defaults — never to the
    // bot-ack pair, whose much shorter ceiling used to age patient replies out of the lens.
    patientReplyThresholdMinutes,
    patientReplyMaxHours,
  } = {},
) {
  switch (normalizeThreadLens(lens)) {
    case "needs": {
      const waiting = getWhatsAppInboxWaitingMinutes(thread, membership, nowMs);
      if (waiting != null) return true;
      const patientReply = getPatientReplyWaitingMinutes(
        thread,
        patientReplyThresholdMinutes,
        patientReplyMaxHours,
        nowMs,
      );
      if (patientReply != null) return true;
      const botAck = getBotAckWaitingMinutes(thread, botAckThresholdMinutes, botAckMaxHours, nowMs);
      return botAck != null;
    }
    // case "mine":
    //   return isAssignedToUser(thread, me);
    default:
      return true;
  }
}

/**
 * Snooze presets for the popover: In 1/2/3 hours · This afternoon (2:00 PM) · Tomorrow 9:00 AM.
 * Each = { id, label, icon name handled by the menu, at: Date, timeLabel }. Presets that are
 * already in the past (e.g. "This afternoon" at 6 PM) are dropped.
 */
export function getSnoozePresets(now = new Date()) {
  const base = new Date(now);

  const inOneHour = new Date(base.getTime() + 60 * 60 * 1000);
  const inTwoHours = new Date(base.getTime() + 2 * 60 * 60 * 1000);
  const inThreeHours = new Date(base.getTime() + 3 * 60 * 60 * 1000);

  const thisAfternoon = new Date(base);
  thisAfternoon.setHours(14, 0, 0, 0);

  const tomorrowMorning = new Date(base);
  tomorrowMorning.setDate(tomorrowMorning.getDate() + 1);
  tomorrowMorning.setHours(9, 0, 0, 0);

  const timeLabel = (d) =>
    d.toLocaleTimeString(undefined, withAppTimeZone({ hour: "numeric", minute: "2-digit" }));

  const presets = [
    { id: "hour", label: "In 1 hour", at: inOneHour, timeLabel: timeLabel(inOneHour) },
    { id: "hours2", label: "In 2 hours", at: inTwoHours, timeLabel: timeLabel(inTwoHours) },
    { id: "hours3", label: "In 3 hours", at: inThreeHours, timeLabel: timeLabel(inThreeHours) },
    { id: "afternoon", label: "This afternoon", at: thisAfternoon, timeLabel: timeLabel(thisAfternoon) },
    { id: "tomorrow", label: "Tomorrow", at: tomorrowMorning, timeLabel: timeLabel(tomorrowMorning) },
  ];
  return presets.filter((p) => p.at.getTime() > base.getTime());
}
